import { createContext, useContext, useMemo, useEffect, useRef } from "react";
import axios from "axios";
import i18n from "../assets/i18n/i18n";
import { USER_DATA } from "../services/storageService/storageKeys";
import { storageService } from "../services/storageService/storageService";

const AxiosContext = createContext(null);

const getSession = () => storageService.getData(USER_DATA) ?? {};
const setSession = (session) => storageService.setData(USER_DATA, session);

export function AxiosProvider({ children }) {
  const baseURL = process.env.REACT_APP_API_BASE_URL || "/api/v1";

  const publicAxios = useMemo(() => axios.create({ baseURL }), [baseURL]);
  const authAxios = useMemo(() => axios.create({ baseURL }), [baseURL]);

  const refreshPromiseRef = useRef(null);

  useEffect(() => {
    const reqPublic = publicAxios.interceptors.request.use((config) => {
      const session = getSession();

      config.headers = config.headers ?? {};
      config.headers["Accept-Language"] = i18n.language || "en";

      if (session?.token) {
        config.headers["Authorization"] = `Bearer ${session.token}`;
      }

      return config;
    });

    const reqAuth = authAxios.interceptors.request.use((config) => {
      const session = getSession();

      config.headers = config.headers ?? {};
      config.headers["Accept-Language"] = i18n.language || "en";

      if (session?.token) {
        config.headers["Authorization"] = `Bearer ${session.token}`;
      }

      return config;
    });

    const resAuth = authAxios.interceptors.response.use(
      (res) => res,
      async (error) => {
        const status = error?.response?.status;
        const original = error?.config;

        if (status !== 401 || !original) {
          return Promise.reject(error);
        }

        if (original._retry) {
          storageService.clearAllData();
          return Promise.reject(error);
        }

        const session = getSession();
        if (!session?.refreshToken) {
          storageService.clearAllData();
          return Promise.reject(error);
        }

        original._retry = true;

        try {
          refreshPromiseRef.current =
            refreshPromiseRef.current ??
            publicAxios.post("/token/refresh", {
              refreshToken: session.refreshToken,
            });

          const refreshResp = await refreshPromiseRef.current;
          refreshPromiseRef.current = null;

          const newAccessToken = refreshResp?.data?.data?.accessToken;
          if (!newAccessToken) {
            throw new Error("No accessToken in refresh response");
          }

          setSession({ ...session, token: newAccessToken });

          original.headers = original.headers ?? {};
          original.headers["Authorization"] = `Bearer ${newAccessToken}`;

          return authAxios(original);
        } catch (e) {
          refreshPromiseRef.current = null;
          storageService.clearAllData();
          return Promise.reject(error);
        }
      }
    );

    return () => {
      publicAxios.interceptors.request.eject(reqPublic);
      authAxios.interceptors.request.eject(reqAuth);
      authAxios.interceptors.response.eject(resAuth);
    };
  }, [publicAxios, authAxios]);

  return (
    <AxiosContext.Provider value={{ publicAxios, authAxios }}>
      {children}
    </AxiosContext.Provider>
  );
}

export function useAxios() {
  const ctx = useContext(AxiosContext);
  if (!ctx) throw new Error("useAxios must be used within AxiosProvider");
  return ctx;
}
